/**
 * Dashboard de Control de Kits — port de kit-stock/src/pages/Dashboard.tsx.
 * Semáforo de estado, entregas pendientes, 4 métricas, próximos ingresos y
 * 7 acciones rápidas en modales. Copys en español verbatim del original.
 */

import { toastError } from '@/lib/toast-error';
import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Plus, Minus, Users, Send, Package, Settings2, AlertTriangle, CheckCircle2, Clock, Check, X, CalendarDays,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { useKits } from './KitsModule';
import { uploadInvoice, validateInvoiceFile } from './invoice';
import { useTranslation } from 'react-i18next';

// --- Modales (mismo archivo, como el original) ---

interface ModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  officeId: string;
}

function useInvalidateKits() {
  const utils = trpc.useUtils();
  return {
    utils,
    summary: (officeId: string) => {
      utils.kits.getOfficeSummary.invalidate({ officeId });
      utils.kits.listOffices.invalidate();
    },
    movements: () => utils.kits.listMovements.invalidate(),
    transfers: () => utils.kits.listTransfers.invalidate(),
    orders: () => utils.kits.listOrders.invalidate(),
    pending: () => utils.kits.listPendingDeliveries.invalidate(),
  };
}

function IntakeModal({ open, onOpenChange, officeId }: ModalProps) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.registerIntake.useMutation();
  const form = useForm({
    resolver: zodResolver(z.object({
      quantity: z.coerce.number().min(1, 'Debe ser al menos 1'),
      note: z.string().optional(),
    })),
    defaultValues: { quantity: 1, note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.ingreso_registrado'));
        inv.summary(officeId);
        inv.movements();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_registrar'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.registrar_ingreso')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.anadir_kits_que_acaban_de_llegar')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.cantidad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('common.ui.save')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryModal({ open, onOpenChange, officeId }: ModalProps) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.registerDelivery.useMutation();
  const form = useForm({
    resolver: zodResolver(z.object({
      personName: z.string().min(1, 'Requerido'),
      quantity: z.coerce.number().min(1, 'Debe ser al menos 1'),
      note: z.string().optional(),
    })),
    defaultValues: { personName: '', quantity: 1, note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.entrega_registrada'));
        inv.summary(officeId);
        inv.movements();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_registrar'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.registrar_entrega')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.entregar_kit_a_una_persona')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="personName" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.persona_que_recibe')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.cantidad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('common.ui.save')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeliveryModal({ open, onOpenChange, officeId }: ModalProps) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.registerBulkDelivery.useMutation();
  const form = useForm({
    resolver: zodResolver(z.object({
      responsible: z.string().min(1, 'Requerido'),
      quantity: z.coerce.number().min(1, 'Debe ser al menos 1'),
      reason: z.string().optional(),
      note: z.string().optional(),
    })),
    defaultValues: { responsible: '', quantity: 1, reason: '', note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.entrega_multiple_registrada'));
        inv.summary(officeId);
        inv.movements();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_registrar'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.entrega_multiple')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.entregar_varios_kits_a_un_responsable')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="responsible" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.responsable_ej_hr')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.cantidad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="reason" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.motivo_opcional')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('common.ui.save')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function TransferModal({ open, onOpenChange, officeId }: ModalProps) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.createTransfer.useMutation();
  const { offices } = useKits();

  const form = useForm({
    resolver: zodResolver(z.object({
      toOfficeId: z.string().min(1, 'Debe seleccionar una oficina destino'),
      quantity: z.coerce.number().min(1, 'Debe ser al menos 1'),
      note: z.string().optional(),
    })),
    defaultValues: { toOfficeId: '', quantity: 1, note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ fromOfficeId: officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.transferencia_creada'));
        inv.summary(officeId);
        inv.transfers();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_crear'),
    });
  };

  const otherOffices = offices.filter((o) => o.officeId !== officeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.transferir_kits')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.enviar_kits_a_otra_clapbase')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="toOfficeId" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.dashboard.oficina_destino')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ''}>
                  <FormControl>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {otherOffices.map((o) => (
                      <SelectItem key={o.officeId} value={o.officeId}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.cantidad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('kits.dashboard.crear_transferencia')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrderModal({ open, onOpenChange, officeId, recommendedOrder }: ModalProps & { recommendedOrder: number }) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.createOrder.useMutation();
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  const form = useForm({
    resolver: zodResolver(z.object({
      quantity: z.coerce.number().min(1, 'Debe ser al menos 1'),
      note: z.string().optional(),
    })),
    defaultValues: { quantity: recommendedOrder > 0 ? recommendedOrder : 10, note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, requested: true, ...values }, {
      onSuccess: async (order) => {
        if (invoiceFile) {
          setUploadingInvoice(true);
          try {
            await uploadInvoice(inv.utils.client, order.orderId, invoiceFile);
            toast.success(t('kits.dashboard.pedido_creado_con_factura_adjunta'));
          } catch (err: any) {
            toastError(err, 'common.toast.el_pedido_se_creo_pero_no_se_pudo_adjuntar_la_factura_podes');
          } finally {
            setUploadingInvoice(false);
          }
        } else {
          toast.success(t('kits.dashboard.pedido_creado'));
        }
        inv.summary(officeId);
        inv.orders();
        onOpenChange(false);
        form.reset();
        setInvoiceFile(null);
      },
      onError: (e) => toastError(e, 'common.toast.error_al_crear'),
    });
  };

  const watchQuantity = form.watch('quantity');
  const supplierMessage = `Hola, ¿cómo va?\n\nTe escribo para hacerte el pedido de ${watchQuantity || 0} kits de onboarding.\n\nQuedo atento por cualquier cosa. ¡Muchas gracias!`;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(supplierMessage);
      toast.success(t('kits.dashboard.mensaje_copiado_al_portapapeles'));
    } catch {
      toast.error(t('kits.dashboard.no_se_pudo_copiar_el_mensaje'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.crear_pedido')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.solicitar_mas_kits_para_esta_oficina')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.dashboard.cantidad_solicitada_recomendado')} {recommendedOrder})</FormLabel>
                <FormControl><Input type="number" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">{t('kits.dashboard.factura_pdf_opcional')}</label>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (file) {
                    const error = validateInvoiceFile(file);
                    if (error) {
                      toast.error(error);
                      e.target.value = '';
                      setInvoiceFile(null);
                      return;
                    }
                  }
                  setInvoiceFile(file);
                }}
              />
              {invoiceFile && <p className="text-xs text-muted-foreground">{t('kits.dashboard.se_adjuntara')} {invoiceFile.name}</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{t('kits.dashboard.mensaje_para_el_proveedor')}</span>
                <Button type="button" variant="outline" size="sm" onClick={copyMessage}>{t('kits.dashboard.copiar_mensaje')}</Button>
              </div>
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground whitespace-pre-line">
                {supplierMessage}
              </div>
            </div>
            <Button type="submit" disabled={mutation.isPending || uploadingInvoice} className="w-full">
              {uploadingInvoice ? t('kits.dashboard.adjuntando_factura') : t('kits.dashboard.solicitar_pedido')}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentModal({ open, onOpenChange, officeId, currentStock }: ModalProps & { currentStock: number }) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.registerAdjustment.useMutation();

  const form = useForm({
    resolver: zodResolver(z.object({
      physicalStock: z.coerce.number().min(0, 'Debe ser mayor o igual a 0'),
      reason: z.enum(['devolucion', 'kit_danado', 'perdida', 'ajuste_manual']),
      note: z.string().optional(),
    })),
    defaultValues: { physicalStock: currentStock, reason: 'ajuste_manual' as const, note: '' },
  });

  const watchPhysical = form.watch('physicalStock');
  const diff = watchPhysical - currentStock;

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.stock_ajustado_correctamente'));
        inv.summary(officeId);
        inv.movements();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_ajustar_stock'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.ajustar_stock')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.ajusta_el_stock_del_sistema_al')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="bg-muted p-3 rounded-md flex justify-between items-center text-sm mb-4">
              <span>{t('kits.dashboard.stock_en_sistema')} <b>{currentStock}</b></span>
              <span>Diferencia: <b className={diff > 0 ? 'text-primary' : diff < 0 ? 'text-destructive' : ''}>{diff > 0 ? `+${diff}` : diff}</b></span>
            </div>
            <FormField control={form.control} name="physicalStock" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.stock_fisico_real')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="reason" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.common.motivo')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="ajuste_manual">{t('kits.dashboard.ajuste_manual')}</SelectItem>
                    <SelectItem value="devolucion">{t('kits.dashboard.devolucion')}</SelectItem>
                    <SelectItem value="kit_danado">{t('kits.dashboard.kit_danado')}</SelectItem>
                    <SelectItem value="perdida">{t('kits.dashboard.perdida')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('kits.dashboard.guardar_ajuste')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function PendingDeliveryModal({ open, onOpenChange, officeId }: ModalProps) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const mutation = trpc.kits.createPendingDelivery.useMutation();
  const form = useForm({
    resolver: zodResolver(z.object({
      personName: z.string().min(1, 'Requerido'),
      note: z.string().optional(),
    })),
    defaultValues: { personName: '', note: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate({ officeId, ...values }, {
      onSuccess: () => {
        toast.success(t('kits.dashboard.entrega_pendiente_registrada'));
        inv.pending();
        onOpenChange(false);
        form.reset();
      },
      onError: (e) => toastError(e, 'common.toast.error_al_registrar'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.registrar_entrega_pendiente')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.anota_a_quien_se_le_debe')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="personName" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.persona_a_la_que_se_le')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.dashboard.nota_opcional')}</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('common.ui.save')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function PendingDeliveriesSection({ officeId, stock }: { officeId: string; stock: number }) {
  const { t } = useTranslation();
  const inv = useInvalidateKits();
  const { data: pending } = trpc.kits.listPendingDeliveries.useQuery({ officeId, status: 'pendiente' });
  const deliverMutation = trpc.kits.deliverPendingDelivery.useMutation();
  const cancelMutation = trpc.kits.cancelPendingDelivery.useMutation();

  const invalidate = () => {
    inv.pending();
    inv.summary(officeId);
    inv.movements();
  };

  if (!pending || pending.length === 0) return null;

  return (
    <div className="rounded-xl border-2 border-[#F7B99E] bg-[#FEF1EC] p-4 shadow-xs">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-5 w-5 text-brand-orange-soft" />
        <h3 className="font-bold text-lg text-brand-orange-soft uppercase tracking-wide">
          {t('kits.dashboard.entregas_pendientes', { count: pending.length })}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        {t('kits.dashboard.personas_que_esperan_su_kit_cuando')}
      </p>
      <ul className="space-y-2">
        {pending.map((p) => (
          <li key={p.pendingId} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-border px-3 py-2">
            <div className="min-w-0">
              <span className="font-medium text-foreground">{p.personName}</span>
              {p.note && <span className="text-sm text-muted-foreground ml-2">{p.note}</span>}
              <span className="block text-xs text-muted-foreground">{t('kits.dashboard.desde_el')} {new Date(p.createdAt).toLocaleDateString(currentLocale())}</span>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                disabled={deliverMutation.isPending || stock < 1}
                title={stock < 1 ? t('kits.dashboard.no_hay_stock_disponible') : undefined}
                onClick={() => deliverMutation.mutate({ pendingId: p.pendingId }, {
                  onSuccess: () => { toast.success(`Kit entregado a ${p.personName}`); invalidate(); },
                  onError: (e) => toastError(e, 'common.toast.error_al_entregar'),
                })}
              >
                <Check className="h-4 w-4 mr-1" /> {t('kits.common.entregar')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate({ pendingId: p.pendingId }, {
                  onSuccess: () => { toast.success(t('kits.dashboard.entrega_pendiente_cancelada')); invalidate(); },
                  onError: (e) => toastError(e, 'common.toast.error_al_cancelar'),
                })}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UpcomingHiresModal({ open, onOpenChange, officeId, officeName }: ModalProps & { officeName?: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = trpc.kits.getUpcomingHires.useQuery({ officeId }, { enabled: open });

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.dashboard.proximos_ingresos')}</DialogTitle>
          <DialogDescription>{t('kits.dashboard.personas_confirmadas_que_van_a_ingresar')}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !data || !data.available ? (
          <p className="text-sm text-muted-foreground py-2">
            {t('kits.dashboard.no_hay_datos_disponibles_de_la')}
          </p>
        ) : data.hires.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            {t('kits.dashboard.no_hay_ingresos_confirmados_con_fecha')}
          </p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {data.hires.map((h, i) => {
              const showClapbase = !!h.clapbase && h.clapbase.trim().toLowerCase() !== (officeName ?? '').trim().toLowerCase();
              return (
                <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <span className="font-medium text-foreground block truncate">{h.name || t('kits.dashboard.sin_nombre')}</span>
                    {showClapbase && (
                      <span className="text-xs text-muted-foreground">{h.clapbase}</span>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0">
                    <CalendarDays className="h-4 w-4" />
                    {formatDate(h.startingDate)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Dashboard principal ---

export function KitsDashboard() {
  const { t } = useTranslation();
  const { activeOfficeId } = useKits();

  const { data: summary, isLoading } = trpc.kits.getOfficeSummary.useQuery(
    { officeId: activeOfficeId! },
    { enabled: !!activeOfficeId }
  );

  const [modalState, setModalState] = useState<{ type: string | null }>({ type: null });

  if (!activeOfficeId) {
    return <div className="p-8 text-center text-muted-foreground">{t('kits.dashboard.seleccione_una_oficina_para_ver_su')}</div>;
  }

  if (isLoading || !summary) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'verde': return 'bg-[#E5F9F7] border-[#A8E4DF] text-primary';
      case 'naranja': return 'bg-[#FEF1EC] border-[#F7B99E] text-brand-orange-soft';
      case 'rojo': return 'bg-red-50 border-red-200 text-red-600';
      default: return 'bg-muted border-border text-foreground';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{summary.name}</h1>
          <p className="text-muted-foreground mt-1">{t('kits.dashboard.resumen_del_estado_de_kits_y')}</p>
        </div>
      </div>

      <div className={`flex items-center gap-4 p-4 rounded-xl border-2 ${getStatusStyles(summary.status)} shadow-xs transition-all`}>
        {summary.status === 'verde' ? <CheckCircle2 className="h-8 w-8" /> : <AlertTriangle className="h-8 w-8" />}
        <div>
          <h3 className="font-bold text-lg leading-tight uppercase tracking-wide">{t('kits.dashboard.estado_de_inventario')}</h3>
          <p className="text-sm font-medium opacity-90">{summary.statusMessage}</p>
        </div>
      </div>

      {summary.openPositionsSource === 'sheet_error' && (
        <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-800 shadow-xs">
          <AlertTriangle className="h-6 w-6 shrink-0" />
          <div>
            <h3 className="font-bold leading-tight">{t('kits.dashboard.no_pudimos_leer_la_hoja_de')}</h3>
            <p className="text-sm opacity-90">
              {t('kits.dashboard.esta_clapbase_tiene_una_hoja_de')}{summary.openPositions}{t('kits.dashboard.que_puede_estar_desactualizado')}
            </p>
          </div>
        </div>
      )}

      <PendingDeliveriesSection officeId={activeOfficeId} stock={summary.stock} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('kits.dashboard.kits_en_stock')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-foreground">{summary.stock}</div>
          </CardContent>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('kits.dashboard.stock_de_seguridad')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-foreground">{summary.safetyStock}</div>
          </CardContent>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('kits.common.ingresando')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-foreground">{summary.incoming}</div>
          </CardContent>
        </Card>
        <Card
          className="shadow-xs cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
          role="button"
          tabIndex={0}
          onClick={() => setModalState({ type: 'upcomingHires' })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalState({ type: 'upcomingHires' }); } }}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('kits.dashboard.posiciones_confirmadas')}</CardTitle>
              {summary.openPositionsSource === 'sheet' ? (
                <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-2 py-0.5 font-medium">{t('kits.common.hoja')}</span>
              ) : summary.openPositionsSource === 'sheet_error' ? (
                <span className="text-xs bg-amber-50 text-amber-700 border border-amber-300 rounded-full px-2 py-0.5 font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {t('kits.dashboard.hoja_sin_conexion')}
                </span>
              ) : (
                <span className="text-xs bg-gray-50 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5 font-medium">{t('kits.common.manual')}</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-foreground">{summary.openPositions}</div>
            <p className="text-xs text-muted-foreground mt-1">{t('kits.dashboard.click_para_ver_proximos_ingresos')}</p>
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-2xl font-bold mb-4">{t('kits.dashboard.acciones_rapidas')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'intake' })}>
            <Plus className="h-5 w-5" /> {t('kits.dashboard.registrar_ingreso')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'delivery' })}>
            <Minus className="h-5 w-5" /> {t('kits.dashboard.registrar_entrega')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'bulk' })}>
            <Users className="h-5 w-5" /> {t('kits.dashboard.entrega_multiple')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'transfer' })}>
            <Send className="h-5 w-5" /> {t('kits.dashboard.transferir_kits')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'order' })}>
            <Package className="h-5 w-5" /> {t('kits.dashboard.crear_pedido')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'adjustment' })}>
            <Settings2 className="h-5 w-5" /> {t('kits.dashboard.ajustar_stock')}
          </Button>
          <Button variant="outline" className="h-24 flex-col gap-2 font-medium" onClick={() => setModalState({ type: 'pending' })}>
            <Clock className="h-5 w-5" /> {t('kits.dashboard.entrega_pendiente')}
          </Button>
        </div>
      </div>

      <IntakeModal open={modalState.type === 'intake'} onOpenChange={(v) => setModalState(v ? { type: 'intake' } : { type: null })} officeId={activeOfficeId} />
      <DeliveryModal open={modalState.type === 'delivery'} onOpenChange={(v) => setModalState(v ? { type: 'delivery' } : { type: null })} officeId={activeOfficeId} />
      <BulkDeliveryModal open={modalState.type === 'bulk'} onOpenChange={(v) => setModalState(v ? { type: 'bulk' } : { type: null })} officeId={activeOfficeId} />
      <TransferModal open={modalState.type === 'transfer'} onOpenChange={(v) => setModalState(v ? { type: 'transfer' } : { type: null })} officeId={activeOfficeId} />
      <CreateOrderModal open={modalState.type === 'order'} onOpenChange={(v) => setModalState(v ? { type: 'order' } : { type: null })} officeId={activeOfficeId} recommendedOrder={summary.recommendedOrder} />
      <PendingDeliveryModal open={modalState.type === 'pending'} onOpenChange={(v) => setModalState(v ? { type: 'pending' } : { type: null })} officeId={activeOfficeId} />
      <UpcomingHiresModal open={modalState.type === 'upcomingHires'} onOpenChange={(v) => setModalState(v ? { type: 'upcomingHires' } : { type: null })} officeId={activeOfficeId} officeName={summary.name} />
      <AdjustmentModal open={modalState.type === 'adjustment'} onOpenChange={(v) => setModalState(v ? { type: 'adjustment' } : { type: null })} officeId={activeOfficeId} currentStock={summary.stock} />
    </div>
  );
}
